/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';

import Retracked from 'js/app/retracked';
import type { TrackingContextData } from 'js/lib/retracked';

import {
  Button,
  Grid,
  Link,
  PageGridContainer,
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  Typography,
  breakpoints,
} from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type { CareerRolesFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import { CAREER_ROLE_CARD_WIDTH } from 'bundles/front-page/common/constants';
import CareerRoleCard from 'bundles/front-page/components/modules/career-roles/CareerRoleCard';
import CareerRolePanel from 'bundles/front-page/components/modules/career-roles/CareerRolePanel';
import Slider from 'bundles/front-page/components/modules/slider/Slider';
import { useMobileOnlyStreamlinedLOHP } from 'bundles/front-page/utils/useStreamlinedLOHP';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/front-page';

const tabBorderWidth = '4px';

const animateIn = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(1px, 100px, 40px);
  }
  100% {
    opacity: 1;
  }
`;

const styles = {
  slideInAnimation: css`
    ${breakpoints.up('md')} {
      opacity: 0;
      animation: 0.8s cubic-bezier(0, 1, 0.9, 1) 0.5s 1 normal forwards running ${animateIn};
    }
  `,
  section: css`
    padding: var(--cds-spacing-800) 0 0;
  `,
  sectionHeader: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  `,
  h1SemiBoldOverride: css`
    font-size: var(--cds-font-size-350);
    margin: 0 var(--cds-spacing-400) var(--cds-spacing-100) 0;

    ${breakpoints.up('sm')} {
      font-size: var(--cds-font-size-400);
    }

    ${breakpoints.down('sm')} {
      margin: 0;
    }
  `,
  tabsContainer: css`
    position: relative;

    && {
      .cds-tab-list-indicator {
        border-radius: 0;
        height: ${tabBorderWidth};
      }
    }
  `,
  tabsTrack: css`
    position: absolute;
    bottom: 0;
    border-bottom: ${tabBorderWidth} solid var(--cds-color-neutral-stroke-primary-weak);
    width: 100%;
  `,
  tabs: css`
    border-bottom: none;
  `,
  tab: css`
    padding-top: var(--cds-spacing-100);
    padding-bottom: var(--cds-spacing-100);
    min-height: 36px;
    font-weight: var(--cds-font-weight-400);

    &[aria-selected='true'] {
      font-weight: var(--cds-font-weight-600);
    }

    :focus {
      outline: unset;
    }
  `,
  careerSliderPageGridContainer: css`
    padding: 0 0 0 var(--cds-spacing-100) !important;
  `,
  streamlinedHeaderCTAContainer: css`
    margin-bottom: calc(var(--cds-spacing-100) * -1) !important;
  `,
  streamlinedSectionHeader: css`
    padding: 0 var(--cds-spacing-100);
  `,
  streamlinedCTA: css`
    padding: var(--cds-spacing-150) var(--cds-spacing-200);

    :hover,
    :focus {
      background-color: initial;
    }

    ::after {
      display: none;
    }
  `,
  careerRoleCardContainer: css`
    padding-right: 0 !important;
    margin: 0 -1vw;
  `,
};

type PropsFromCaller = {
  roles?: Array<CareerRolesFieldsFragment | null>;
  countryCode?: string;
};

type PropsFromGetContext = {
  _eventData: TrackingContextData;
};

type Props = PropsFromGetContext & PropsFromCaller;

export const CareerRoles = ({ roles, countryCode, _eventData }: Props) => {
  const isStreamlinedLOHPTreatment = useMobileOnlyStreamlinedLOHP() !== 'control';

  const filteredRoles = roles?.filter((role) => !!role && !!role?.title) as Array<CareerRolesFieldsFragment>;

  const [currentRole, setCurrentRole] = React.useState<CareerRolesFieldsFragment>(filteredRoles?.[0]);

  const handleChange = (newValue: string) => {
    const newRole = filteredRoles.find((role) => newValue === role?.sys.id);
    if (newRole) {
      setCurrentRole(newRole);

      Retracked.trackComponent(
        _eventData,
        {
          selectedRole: newRole.title,
        },
        'ca_module_role',
        'click'
      );
    }
  };

  if (!filteredRoles.length) {
    return null;
  }

  const ctaProps = {
    href: '/career-academy?trk_ref=camodule',
    target: '_blank',
    icon: <ArrowNextIcon />,
    rel: 'noopener noreferrer',
    typographyVariant: 'bodyPrimary' as const,
    component: TrackedLink2,
    trackingName: 'ca_module_view_all_roles',
    iconPosition: 'after' as const,
  };

  const ctaLabel = _t('View all roles');

  return (
    <section css={styles.section}>
      <div css={styles.slideInAnimation}>
        <PageGridContainer
          component={TrackedDiv}
          trackingName="ca_module"
          withVisibilityTracking={true}
          requireFullyVisible={false}
          trackClicks={false}
          css={isStreamlinedLOHPTreatment ? styles.careerSliderPageGridContainer : ''}
        >
          <Grid
            container
            item
            xs={12}
            spacing={0}
            css={isStreamlinedLOHPTreatment ? styles.streamlinedHeaderCTAContainer : ''}
          >
            <Grid
              item
              xs={12}
              css={[styles.sectionHeader, isStreamlinedLOHPTreatment ? styles.streamlinedSectionHeader : '']}
            >
              <Typography variant="h1semibold" component="h2" css={styles.h1SemiBoldOverride}>
                {_t('Launch a new career in as little as 6 months')}
              </Typography>
              {!isStreamlinedLOHPTreatment ? (
                <Link
                  {...ctaProps}
                  variant="quiet"
                  style={{
                    // use inline style to override CDS's link color
                    color: `var(--cds-color-neutral-primary)`,
                  }}
                >
                  {ctaLabel}
                </Link>
              ) : (
                <Button {...ctaProps} variant="ghost" edgeAlign="start" css={styles.streamlinedCTA}>
                  {ctaLabel}
                </Button>
              )}
            </Grid>
            {!isStreamlinedLOHPTreatment && (
              <Grid item xs={12}>
                <TabContext value={currentRole.sys.id}>
                  <div css={styles.tabsContainer}>
                    <div css={styles.tabsTrack} />
                    <Tabs
                      aria-label={_t('Career Role Tabs')}
                      onChange={handleChange}
                      variant="section"
                      css={styles.tabs}
                    >
                      {filteredRoles.map((role) => (
                        <Tab label={role.title as string} value={role.sys.id} css={styles.tab} key={role.sys.id} />
                      ))}
                    </Tabs>
                  </div>
                  <TabPanel value={currentRole.sys.id}>
                    <CareerRolePanel role={currentRole} countryCode={countryCode} />
                  </TabPanel>
                </TabContext>
              </Grid>
            )}
          </Grid>
          {isStreamlinedLOHPTreatment && (
            <Grid item xs={12} css={styles.careerRoleCardContainer}>
              <Slider forcedCardWidth={CAREER_ROLE_CARD_WIDTH}>
                {filteredRoles.map((role) => (
                  <CareerRoleCard role={role} countryCode={countryCode} key={role.sys.id} />
                ))}
              </Slider>
            </Grid>
          )}
        </PageGridContainer>
      </div>
    </section>
  );
};

export default memo(
  compose<Props, PropsFromCaller>(
    getContext({
      _eventData: PropTypes.object,
    })
  )(CareerRoles)
);
